import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import type { Item, Props as AutoCompleteProps } from './AutoComplete';
import { AutoComplete } from './AutoComplete';

const DEBOUNCE_TIME = 400;

type Props = {
  items: Item[];
  getOptions: (value: string) => Promise<unknown>;
  autoopen?: boolean;
  autoclear?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  inputId?: string;
  $isBorderlessStyle?: boolean;
  placeholder?: string;
  maxHeight?: AutoCompleteProps['maxHeight'];
  hasErrors?: AutoCompleteProps['hasErrors'];
};

const AutoCompleteContainer: React.FC<Props> = ({
  items,
  getOptions,
  autoopen = true,
  autoclear = false,
  disabled = false,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectionIndex, setSelectionIndex] = useState<number | null>(null);
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const cancel = () => {
    setInputValue('');
    setOpen(false);
  };

  const fetchItems = async (value: string): Promise<void> => {
    setLoading(true);
    try {
      await getOptions(value.trim());
      setLoading(false);
      setOpen(true);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setLoading(false);
        throw error;
      }
    }
  };

  // We need to cache the debounced fetchItems function between renderings otherwise
  // the fetchItems would not be debounced as on every rendering a new debounced function
  // would be created. Also we need to cancel the debounced function after unmounting the component.
  // The useCallback is recreated when getOptions prop is updated. We currently have the use case
  // when selecting a tenant to blacklist, the blacklisted tenant should disappear from the
  // autocomplete menu. This would not happen if we don't recreate the callback.
  const fetchItemsDebounced = useCallback(
    debounce(fetchItems, DEBOUNCE_TIME),
    [getOptions],
  );

  const onInputChange = async (value: string): Promise<void> => {
    setInputValue(value);
    await fetchItemsDebounced(value);
  };

  const getFirstSelectionIndex = () => {
    for (let index = 0; index < items.length; index += 1) {
      if (items[index]?.onSelect) {
        return index;
      }
    }

    return 0;
  };

  const increaseSelectionIndex = (currentIndex: number): number | null => {
    const index = currentIndex + 1;

    if (index >= items.length) {
      setSelectionIndex(items.length - 1);
      return items.length;
    }

    if (!items[index]?.onSelect) {
      return increaseSelectionIndex(index);
    }

    setSelectionIndex(index);
    return index;
  };

  const decreaseSelectionIndex = (currentIndex: number): number | null => {
    const index = currentIndex - 1;

    if (index <= 0) {
      const firstSelectionIndex = getFirstSelectionIndex();
      setSelectionIndex(firstSelectionIndex);
      return firstSelectionIndex;
    }

    if (!items[index]?.onSelect) {
      return decreaseSelectionIndex(index);
    }

    setSelectionIndex(index);
    return index;
  };

  const selectItem = (item: Item, multiSelectMode: boolean) => {
    if (!item.onSelect) {
      return;
    }

    if (!multiSelectMode) {
      cancel();
    }

    item.onSelect();
  };

  const scrollToSelectedItem = (index: number | null) => {
    if (!index) { return; }

    const selectedItem = items[index];
    if (!selectedItem?.ref?.parentElement) { return; }

    selectedItem.ref.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!items.length) { return; }

    if (!isOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!disabled && selectionIndex !== null) {
          scrollToSelectedItem(increaseSelectionIndex(selectionIndex));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!disabled && selectionIndex !== null) {
          scrollToSelectedItem(decreaseSelectionIndex(selectionIndex));
        }
        break;
      case 'Escape':
        cancel();
        break;
      case 'Enter': {
        event.preventDefault();
        if (!disabled && selectionIndex !== null) {
          const selectedItem = items[selectionIndex];
          if (selectedItem) {
            selectItem(selectedItem, event.shiftKey);
          }
          scrollToSelectedItem(decreaseSelectionIndex(selectionIndex));
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => (
    // Returning a function to be executed on
    // componentWillUnmount to cancel debounce.
    () => {
      fetchItemsDebounced.cancel();
    }
  ), [fetchItemsDebounced]);

  // Executed after each render.
  // Scrolls to the selected item if there is any.
  // Selects the first selectable item otherwise.
  useEffect(() => {
    if (isOpen) {
      if (selectionIndex == null) {
        setSelectionIndex(getFirstSelectionIndex());
      }
    }
  });

  const onFocus = () => {
    if (autoopen) {
      onInputChange(inputValue);
    }
  };

  const onBlur = () => {
    if (autoclear) {
      setInputValue('');
    }
    setOpen(false);
  };

  const onHighlightItem = (idx: number) => {
    if (!disabled && items[idx]?.onSelect) {
      setSelectionIndex(idx);
    }
  };

  const onSelectItem = (item: Item, shiftKey: boolean) => {
    if (!disabled) {
      selectItem(item, shiftKey);
    }
  };

  const isItemHighlighted = (idx: number) => idx === selectionIndex;

  return (
    <AutoComplete
      inputValue={inputValue}
      onKeyDown={onKeyDown}
      onInputChange={onInputChange}
      onFocus={onFocus}
      onBlur={onBlur}
      isLoading={isLoading}
      onCancel={cancel}
      isOpen={isOpen}
      items={items}
      onHighlightItem={onHighlightItem}
      isItemHighlighted={isItemHighlighted}
      onSelectItem={onSelectItem}
      disabled={disabled}
      {...rest}
    />
  );
};

export { AutoCompleteContainer };
export type { Props, Item };
